/**
 * Error response format from API
 */
interface ApiErrorResponse {
  message?: string
  error?: string
  errors?: Record<string, string[]>
}

const FIELD_LABELS: Record<string, string> = {
  limit: 'result count',
  latitude: 'latitude',
  longitude: 'longitude',
  schedule: 'schedule',
  store_id: 'store',
  date: 'date',
  fe_user_id: 'field engineer',
  fe_user_ids: 'field engineers',
}

/**
 * Turn Laravel-style validation text into short, user-friendly copy.
 */
export function humanizeValidationMessage(field: string, message: string): string {
  const label = FIELD_LABELS[field] ?? field.replace(/_/g, ' ')
  const normalized = message.trim()

  if (/must not be greater than (\d+)/i.test(normalized)) {
    const max = normalized.match(/must not be greater than (\d+)/i)?.[1]
    if (field === 'limit') {
      return `We can only show up to ${max} field engineers at a time. Please try again.`
    }
    return `Please enter a ${label} of ${max} or less.`
  }

  if (/must be at least (\d+)/i.test(normalized)) {
    const min = normalized.match(/must be at least (\d+)/i)?.[1]
    return `Please enter a ${label} of at least ${min}.`
  }

  if (/is required/i.test(normalized)) {
    return `Please provide a valid ${label}.`
  }

  if (/must be a number|must be an integer/i.test(normalized)) {
    return `Please enter a valid number for ${label}.`
  }

  if (/must be a valid/i.test(normalized) || /format is invalid/i.test(normalized)) {
    return `Please enter a valid ${label}.`
  }

  // Fallback: drop technical "The X field ..." wording when present.
  return normalized
    .replace(/^The\s+/i, '')
    .replace(/\s+field\s+/i, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * Build one user-facing message from an API error payload (incl. validation errors).
 */
export function formatApiErrorMessage(
  errorData: ApiErrorResponse | null | undefined,
  fallback = 'Something went wrong. Please try again.',
): string {
  if (!errorData) return fallback

  if (errorData.errors && Object.keys(errorData.errors).length > 0) {
    const messages = Object.entries(errorData.errors).flatMap(([field, msgs]) =>
      (msgs ?? []).map((msg) => humanizeValidationMessage(field, msg)),
    )
    if (messages.length > 0) return messages.join(' ')
  }

  const raw = errorData.message || errorData.error
  if (!raw) return fallback

  if (/the given data was invalid/i.test(raw) || /^validation failed$/i.test(raw.trim())) {
    return 'Some of the information provided is invalid. Please check and try again.'
  }

  return raw
}

/**
 * Extracts error message from API error response
 * Handles both simple error objects and validation error responses
 */
export function getErrorMessage(error: any): string {
  // Check if it's an Axios error with response data
  if (error?.response?.data) {
    return formatApiErrorMessage(error.response.data as ApiErrorResponse, error?.message)
  }

  // Fallback to error message or default
  return error?.message || error?.toString() || 'An unexpected error occurred'
}

/**
 * Formats error response for toast notification
 * Returns title, description, and error list for better UX
 */
export function formatErrorForToast(error: any): {
  title: string
  description: string
  errors?: string[]
} {
  // Plain string from callers (e.g. service message already humanized)
  if (typeof error === 'string') {
    return {
      title: 'Unable to continue',
      description: error,
    }
  }

  if (error?.response?.data) {
    const errorData: ApiErrorResponse = error.response.data

    if (errorData.errors && Object.keys(errorData.errors).length > 0) {
      const allErrors = Object.entries(errorData.errors).flatMap(([field, messages]) =>
        (messages ?? []).map((msg) => humanizeValidationMessage(field, msg)),
      )

      return {
        title: 'Unable to load recommendations',
        description: '',
        errors: allErrors,
      }
    }

    if (errorData.message || errorData.error) {
      return {
        title: 'Unable to continue',
        description: formatApiErrorMessage(errorData),
      }
    }
  }

  // Fallback
  const message = error?.message || error?.toString() || 'An unexpected error occurred'
  return {
    title: 'Error',
    description: message,
  }
}

